import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as HistoryIcon } from 'assets/icons/history.svg';
import { clsx } from 'clsx';
import AiChat from 'components/AiChat/AiChat';
import useAiChatSession from 'components/AiChat/hooks/useAiChatSession';
import AiChatHistory from 'components/AiChatHistory/AiChatHistory';
import Button from 'components/Button/Button';
import DraggablePanel from 'components/DraggablePanel/DraggablePanel';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import IconButton from 'components/IconButton/IconButton';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { useAiChatPanel } from 'contexts/AiChatPanelContext';
import { useResolvedAiScreenContext } from 'contexts/AiScreenContext';

import type IAiChatScreen from 'components/AiChat/interfaces/IAiChatScreen';
import type { IDraggablePanelSize } from 'components/DraggablePanel/interfaces/IDraggablePanel';
import type { ReactElement } from 'react';

import styles from './sass/AiChatPanel.module.scss';

const MAX_SIZE: IDraggablePanelSize = { width: 720, height: 880 };
const MIN_SIZE: IDraggablePanelSize = { width: 360, height: 480 };
/** Matches the floating panel's default width, so docking doesn't jump the chat to a new size. */
const DOCKED_WIDTH = 420;

/**
 * The assistant, in a panel over whatever screen the user is on.
 *
 * @see [3. Components](../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 * @see [7. Flow: chat list and history](../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 *
 * Mounted once by the page layout, so the chat outlives every route change: hiding the panel
 * leaves the messages where they were. The screen it is over is resolved on each render and handed
 * to the chat, which shows it with the message box and sends it with each message.
 */
function AiChatPanel(): ReactElement {
  const { t } = useTranslation();
  const {
    isVisible, position, size, isAttached, closePanel, movePanel, resizePanel, toggleAttached,
  } = useAiChatPanel();
  const screenContext = useResolvedAiScreenContext();
  const {
    chatId, conversationKey, adoptCreatedChat, selectChat, startNewChat, dropChat,
  } = useAiChatSession();
  const [isHistoryOpen, setIsHistoryOpen] = useState<boolean>(false);

  const screenLabel = screenContext.title
    || t(`aiChat.screens.${screenContext.screen}`, { defaultValue: '' });
  // The chat gets the whole context, with the one thing it cannot resolve itself added: the
  // screen's name. An unregistered screen is named by its id, which is its path — the assistant
  // is better off with that than with no idea where the user is.
  const screen = useMemo<IAiChatScreen>(
    () => ({ ...screenContext, label: screenLabel || screenContext.screen }),
    [screenLabel, screenContext],
  );

  const handleToggleHistory = () => setIsHistoryOpen((current) => !current);

  const handleSelectChat = (nextChatId: number) => {
    selectChat(nextChatId);
    setIsHistoryOpen(false);
  };

  // Both ways out of the history land on the chat view, so the panel never sits on a list the
  // user has already chosen from.
  const handleNewChat = () => {
    startNewChat();
    setIsHistoryOpen(false);
  };

  // Shown in place of the chat or the history when it throws. The panel's own header stays up, so
  // the user can still close or move it, or open the history; trying again mounts the content
  // afresh on the same chat.
  const renderFailed = (retry: () => void) => (
    <div className={styles.failed} role="alert">
      <p>{t('aiChat.panelFailed')}</p>
      <Button variant={BUTTON_VARIANT.secondary} onClick={retry} title={t('aiChat.tryAgain')} />
    </div>
  );

  return (
    <DraggablePanel
      isVisible={isVisible}
      position={position}
      size={size}
      minSize={MIN_SIZE}
      maxSize={MAX_SIZE}
      contentClassName={styles.content}
      onMove={movePanel}
      onResize={resizePanel}
      title={t('aiChat.panelTitle')}
      headerActions={(
        <IconButton
          icon={<HistoryIcon />}
          onClick={handleToggleHistory}
          ariaLabel={isHistoryOpen ? t('aiChat.backToChat') : t('aiChat.openHistory')}
        />
      )}
      onClose={closePanel}
      closeLabel={t('aiChat.hidePanel')}
      isAttached={isAttached}
      dockedWidth={DOCKED_WIDTH}
      onToggleAttach={toggleAttached}
      toggleAttachLabel={isAttached ? t('aiChat.floatPanel') : t('aiChat.dockPanel')}
    >
      {/* Hidden rather than unmounted, so opening the history mid-reply neither drops the reply
          being streamed nor the place the reader had scrolled to. */}
      <div className={clsx(styles.view, { [styles.viewHidden]: isHistoryOpen })}>
        {/* Keyed by conversation, so a chat that cannot be rendered is left behind by picking
            another: the boundary for the next one starts clean rather than holding the old error.
            Adopting the chat a send created keeps the key, so the reply that created it stays. */}
        <ErrorBoundary key={conversationKey} fallback={renderFailed}>
          <AiChat
            chatId={chatId}
            conversationKey={conversationKey}
            screen={screen}
            onChatCreated={adoptCreatedChat}
            className={styles.chat}
          />
        </ErrorBoundary>
      </div>

      {/* Its own boundary, so the history stays the way out of a chat that cannot be rendered. */}
      {isHistoryOpen ? (
        <ErrorBoundary fallback={renderFailed}>
          <AiChatHistory
            selectedChatId={chatId}
            onSelect={handleSelectChat}
            onNewChat={handleNewChat}
            onDeleted={dropChat}
          />
        </ErrorBoundary>
      ) : null}
    </DraggablePanel>
  );
}

export default AiChatPanel;
